import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAdminGuard } from '../../hooks/useAdminGuard';
import { getSiteSettings, saveSiteSettings, uploadHeroImage, SiteSettings } from '../../config/firebase';

const Settings = () => {
  const { user, loading, isAdmin } = useAdminGuard({ redirectIfNotAdmin: true });
  const [form, setForm] = useState<Partial<SiteSettings>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAdmin) return;
    getSiteSettings().then(setForm).catch(() => {});
  }, [isAdmin]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { url, path } = await uploadHeroImage(file, form.heroImagePath);
      const next = { ...form, heroImageUrl: url, heroImagePath: path };
      setForm(next);
      await saveSiteSettings({ heroImageUrl: url, heroImagePath: path });
      toast.success('Hero image updated');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveSiteSettings({
        contactAddress: form.contactAddress || '',
        contactEmail: form.contactEmail || '',
        contactPhones: form.contactPhones || '',
      });
      toast.success('Settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user || !isAdmin) return (
    <div className="flex justify-center items-center h-40">
      <svg className="animate-spin h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <span className="ml-3 text-teal-700 text-sm">Loading settings...</span>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-teal-700 mb-8">Site Settings</h1>

      <section className="bg-white p-6 rounded-lg shadow border border-stone-200 mb-8">
        <h2 className="text-xl font-semibold text-stone-800 mb-4">Hero Background Image</h2>
        <img
          src={form.heroImageUrl || 'https://via.placeholder.com/800x300?text=Default+hero+image'}
          alt="Current hero"
          className="w-full h-48 object-cover rounded mb-4 bg-stone-100"
        />
        <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={handleHeroUpload} />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded"
        >
          {uploading ? 'Uploading…' : 'Change Hero Image'}
        </button>
        <p className="text-sm text-stone-500 mt-2">Leave unchanged to keep the built-in default image.</p>
      </section>

      <form onSubmit={handleSave} className="bg-white p-6 rounded-lg shadow border border-stone-200">
        <h2 className="text-xl font-semibold text-stone-800 mb-4">Contact Us</h2>
        <label className="block mb-4">
          <span className="text-stone-700 font-medium">Address</span>
          <textarea name="contactAddress" value={form.contactAddress || ''} onChange={handleChange} rows={2}
            className="mt-1 block w-full rounded border-stone-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
        </label>
        <label className="block mb-4">
          <span className="text-stone-700 font-medium">Email</span>
          <input name="contactEmail" value={form.contactEmail || ''} onChange={handleChange} type="text"
            className="mt-1 block w-full rounded border-stone-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
        </label>
        <label className="block mb-6">
          <span className="text-stone-700 font-medium">Phone(s)</span>
          <input name="contactPhones" value={form.contactPhones || ''} onChange={handleChange} type="text"
            className="mt-1 block w-full rounded border-stone-300 shadow-sm focus:border-teal-500 focus:ring-teal-500" />
        </label>
        <button type="submit" disabled={saving}
          className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold px-6 py-2 rounded">
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};

export default Settings;
